import React from "react";

import {Dialog, Transition} from "@headlessui/react";
import ReactModal from "react-modal";

import {zIndexes} from "@client/style";

namespace styles {
    export const modal = {
        overlay: {
            zIndex: zIndexes.modal,
        },
        content: {
            maxWidth: "calc(100vw - 20px)",
            maxHeight: "calc(100vh - 80px)", // Generally want the top bar visible.
            bottom: "auto",
            left: "50%",
            marginRight: "-50%",
            right: "auto",
            top: "50%",
            transform: "translate(-50%, -50%)",
        },
    };
}

interface Props {
    contentLabel: string;
    isOpen: boolean;
    onRequestClose: () => void;
    children?: React.ReactNode;
}

export const Modal = (props: Props) => (
    <ReactModal
        contentLabel={props.contentLabel}
        isOpen={props.isOpen}
        style={styles.modal}
        onRequestClose={props.onRequestClose}
        ariaHideApp={false}
    >
        <div style={{display: "flex", flexDirection: "column", gap: 10}}>
            {props.children}
        </div>
    </ReactModal>
);

export function HModal(props: {
    isOpen: boolean;
    setOpen: (state: boolean) => void;
    children: React.ReactNode;
}) {
    return (
        <Transition.Root show={props.isOpen} as={React.Fragment}>
            {/* z-index above the left hand menu on the manager side */}
            <Dialog as="div" className="relative z-50" onClose={props.setOpen}>
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-4 top-24 z-10">
                    <Transition.Child
                        as={React.Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <Dialog.Panel
                            className="
                                flex min-h-full flex-col rounded-lg bg-white
                                sm:mx-auto sm:max-w-2xl
                            "
                        >
                            {props.children}
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

HModal.Content = (props: {
    title: React.ReactNode;
    content: React.ReactNode;
    actions: React.ReactNode;
}) => (
    <>
        <div className="order-3 p-4">{props.actions}</div>
        <div className="relative order-2 flex-1">
            <div className="absolute inset-0 overflow-y-auto px-4">{props.content}</div>
        </div>
        <div className="order-1 p-4">{props.title}</div>
    </>
);

export function ContentModal(props: {
    isOpen: boolean;
    setOpen: (state: boolean) => void;
    children: React.ReactNode;
}) {
    return (
        <Transition.Root show={props.isOpen} as={React.Fragment}>
            {/* z-index above the left hand menu on the manager side */}
            <Dialog as="div" className="relative z-50" onClose={props.setOpen}>
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div
                        className="
                            flex min-h-full items-end justify-center p-4 text-center
                            sm:items-center sm:p-0
                        "
                    >
                        <Transition.Child
                            as={React.Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel
                                className="
                                    relative w-full transform overflow-hidden rounded-lg
                                    bg-white text-left shadow-xl transition-all
                                    sm:my-8 sm:max-w-[800px]
                                "
                            >
                                {props.children}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

export function BasicModal(props: {
    isOpen: boolean;
    setOpen: (state: boolean) => void;
    children: React.ReactNode;
}) {
    return (
        <Transition.Root show={props.isOpen} as={React.Fragment}>
            {/* z-index above the left hand menu on the manager side */}
            <Dialog as="div" className="relative z-50" onClose={props.setOpen}>
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
                </Transition.Child>

                <div className="inset-safe fixed z-10 w-screen overflow-y-auto">
                    <div
                        className="
                            flex min-h-full items-end justify-center p-4 text-center
                            sm:items-center sm:p-0
                        "
                    >
                        <Transition.Child
                            as={React.Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel
                                className="
                                    relative transform overflow-hidden rounded-lg
                                    bg-white text-left shadow-xl transition-all
                                "
                            >
                                {props.children}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
